import { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Check, Layers, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import { useGetPostByUserIdQuery } from '@/redux/post/post.api.slice';
import {
  toggleProjectSelection,
  selectSelectedProjectIds,
} from '@/redux/projectGroup/projectGroup.slice';
import {
  IPostWithUserAndAttachments,
  IPostVisibility,
} from '@/redux/post/post.d';
import { RootState } from '@/redux/store';

interface IProjectSelectorProps {
  error?: string;
}

function ProjectSelector({ error }: IProjectSelectorProps) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const selectedProjectIds = useSelector((state: RootState) =>
    selectSelectedProjectIds(state)
  );
  const [searchQuery, setSearchQuery] = useState('');

  const userId = currentUser.user?._id || '';

  const {
    data: posts,
    isLoading,
    isError,
  } = useGetPostByUserIdQuery(userId, {
    skip: !userId,
  });

  // Filter posts based on search query, exclude projects in groups, and sort by latest first
  const filteredPosts = useMemo(() => {
    if (!posts) return [];

    // First, filter out projects that are already in a group
    // AND only include public and private projects
    let availablePosts = posts.filter(
      (post) =>
        !post.projectGroupId &&
        (post.visibility === IPostVisibility.PUBLIC ||
          post.visibility === IPostVisibility.PRIVATE)
    );

    // Then apply search filter if there's a query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      availablePosts = availablePosts.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.description?.toLowerCase().includes(query)
      );
    }

    // Sort by createdAt descending (latest first)
    return availablePosts.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Descending order (latest first)
    });
  }, [posts, searchQuery]);

  const handleToggleProject = (projectId: string) => {
    dispatch(toggleProjectSelection(projectId));
  };

  const getThumbnailUrl = (post: IPostWithUserAndAttachments): string => {
    return post.postThumbnail || '/placeholder-project.png';
  };

  const isProjectInGroup = (post: IPostWithUserAndAttachments): boolean => {
    return !!post.projectGroupId;
  };

  const isSelected = (projectId: string): boolean => {
    return selectedProjectIds.includes(projectId);
  };

  const getProjectButtonClassName = (
    inGroup: boolean,
    selected: boolean
  ): string => {
    const baseClasses =
      'w-full text-left p-3 rounded-lg border border-l-4 border-l-transparent transition-[color,background-color,border-color,box-shadow]';
    if (inGroup) {
      return `${baseClasses} opacity-50 cursor-not-allowed bg-muted border-border`;
    }
    if (selected) {
      return `${baseClasses} bg-white border-l-emerald-500 border-t border-r border-b border-t-gray-200 border-r-gray-200 border-b-gray-200 shadow-sm`;
    }
    return `${baseClasses} bg-white border-border hover:border-gray-300 hover:shadow-sm cursor-pointer`;
  };

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Select Projects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Select Projects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-500">Failed to load your projects.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Select Projects <span className="text-red-500">*</span>
          </div>
          {selectedProjectIds.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {selectedProjectIds.length} selected
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        {/* Projects List */}
        <div className="max-h-[400px] overflow-y-auto space-y-2 custom-scrollbar">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {posts && posts.length === 0
                ? 'You have no projects yet. Create some projects first.'
                : 'No projects match your search.'}
            </div>
          ) : (
            filteredPosts.map((post) => {
              const inGroup = isProjectInGroup(post);
              const selected = isSelected(post._id);
              const disabled = inGroup;

              return (
                <button
                  key={post._id}
                  type="button"
                  onClick={() => !disabled && handleToggleProject(post._id)}
                  disabled={disabled}
                  className={getProjectButtonClassName(inGroup, selected)}
                >
                  <div className="flex items-center gap-3">
                    {/* Thumbnail */}
                    <div className="relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden bg-muted">
                      <img
                        src={getThumbnailUrl(post)}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/placeholder-project.png';
                        }}
                      />
                      {/* Selection indicator */}
                      {selected && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <Check className="h-6 w-6 text-primary" />
                        </div>
                      )}
                    </div>

                    {/* Project Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium line-clamp-2">{post.title}</h4>
                      {post.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {post.description}
                        </p>
                      )}
                      {inGroup && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          Already in a group
                        </Badge>
                      )}
                    </div>

                    {/* Selection checkbox visual */}
                    {!disabled && (
                      <div
                        className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center ${
                          selected
                            ? 'bg-emerald-500 border-emerald-500'
                            : 'border-muted-foreground'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Help text */}
        <p className="text-xs text-muted-foreground">
          Select one or more projects to include in this group. Projects already
          in a group are automatically filtered out.
        </p>
      </CardContent>
    </Card>
  );
}

export default ProjectSelector;
